<div class="jurisdiction-risk-config-form">
  @if (isLoading) {
    <div class="loading-container">
      <app-rings-loader class="bg-transparent"></app-rings-loader>
    </div>
  }

  @if (!isLoading) {
    <div class="config-container">
      <!-- Context Strip -->
      <div class="context-strip">
        <span class="source-badge" [ngClass]="{ 'jurisdiction': isCustomConfig, 'tenant': !isCustomConfig }">
          <span class="material-icons">{{ isCustomConfig ? 'tune' : 'apartment' }}</span>
          {{ isCustomConfig ? ('Jurisdiction Override' | translate) : ('Using Tenant Defaults' | translate) }}
        </span>
        <div class="spacer"></div>
        <ng-container *objectHasPermission="riskConfigPermissionObject; action: createAction">
          @if (!isCustomConfig) {
            <button class="btn btn-outline" (click)="createFromDefaults()">
              <span class="material-icons">add_circle_outline</span> {{ "Create from Defaults" | translate }}
            </button>
          }
        </ng-container>
        <ng-container *objectHasPermission="riskConfigPermissionObject; action: deleteAction">
          @if (isCustomConfig) {
            <button class="btn btn-outline" (click)="deleteConfiguration()">
              <span class="material-icons">restart_alt</span> {{ "Reset to Defaults" | translate }}
            </button>
          }
        </ng-container>
      </div>
      <!-- ═══ EN 689 COMPLIANCE TESTING CARD ═══ -->
      @if (!bayesianOnly) {
        <div class="section-card en689-card">
          <div class="section-card-header">
            <div>
              <div class="section-card-title">
                <span class="material-icons">verified</span>
                {{ "EN 689 Compliance Testing" | translate }}
              </div>
            </div>
            <span class="section-card-subtitle">{{ "This setting applies to this jurisdiction only." | translate }}</span>
          </div>
          <div class="en689-toggle-row">
            <mat-slide-toggle [(ngModel)]="useEN689" (ngModelChange)="onConfigChanges()"
              [disabled]="!canEditRiskConfig">
              {{ 'Apply EN 689 Compliance Testing' | translate }}
            </mat-slide-toggle>
          </div>
          <!-- DASH-3163: state what each toggle position means, since it silently decides whether the
          per-band Min Samp column below has any effect. -->
          <div class="en689-explainer">
            <div class="en689-explainer-item" [class.active]="useEN689">
              <span class="en689-explainer-label">{{ "On" | translate }}</span>
              <span class="en689-explainer-text">{{ "Sample adequacy follows the EN 689 standard — a preliminary test at 3, 4 or 5 samples with tightened thresholds, otherwise 6 samples for the statistical test. The per-band Min Samp values below are not used." | translate }}</span>
            </div>
            <div class="en689-explainer-item" [class.active]="!useEN689">
              <span class="en689-explainer-label">{{ "Off" | translate }}</span>
              <span class="en689-explainer-text">{{ "No compliance verdict is issued. The per-band Min Samp values below govern the required sample count." | translate }}</span>
            </div>
            <div class="en689-explainer-note">
              <span class="material-icons">info_outline</span>
              {{ "The decision statistic is chosen at the time of risk assessment and is unaffected by this toggle." | translate }}
            </div>
          </div>
        </div>
      }
      <!-- ═══ PIPELINE INDICATOR CARD ═══ -->
      @if (!bayesianOnly) {
        <div class="pipeline-card">
          <div class="pipeline-flow">
            @for (node of pipelineNodes; track trackByIndex($index); let last = $last) {
              <div class="pipeline-node" (click)="scrollToSection(node.id)">
                <span class="material-icons">{{ node.icon }}</span>
                <span class="pipeline-node-label">{{ node.label | translate }}</span>
              </div>
              @if (!last) {
                <div class="pipeline-line">
                  <span class="material-icons">chevron_right</span>
                </div>
              }
            }
          </div>
          <div class="pipeline-actions">
            <button class="btn btn-text pipeline-hiw-btn" (click)="togglePipelineHIW()">
              <span class="material-icons">{{ showPipelineHIW ? 'expand_less' : 'help_outline' }}</span>
              {{ "How the Pipeline Works" | translate }}
            </button>
          </div>
        </div>
      }
      <!-- Pipeline How It Works Panel -->
      @if (!bayesianOnly) {
        <div class="hiw-content pipeline-hiw" [class.open]="showPipelineHIW">
          <div class="hiw-inner">
            <div class="hiw-grid">
              <div class="hiw-section">
                <h4><span class="material-icons">assignment</span> {{ "Qualitative Assessment" | translate }}</h4>
                <p>{{ "Used when sampling data is not yet available." | translate }}</p>
                <div class="formula-block">{{ "Exceedance" | translate }} (1–{{ matrixExceedanceDimension }}) × {{ "Frequency" | translate }} (1–{{ matrixFrequencyDimension }}) <span class="equals">=</span> {{ "Qual Score" | translate }} (1–{{ maxQualitativeProduct }})</div>
                <ol class="step-list">
                  <li>{{ "Assessor rates Exceedance Probability and Frequency of Exposure" | translate }}</li>
                  <li>{{ "System multiplies them to produce a Qualitative Score" | translate }}</li>
                  <li>{{ "Score matched against Qual Thresholds to assign a Likelihood Level" | translate }}</li>
                </ol>
              </div>
              <div class="hiw-section">
                <h4><span class="material-icons">science</span> {{ "Quantitative Assessment" | translate }}</h4>
                <p>{{ "Used when sufficient air samples have been collected and analyzed." | translate }}</p>
                <div class="formula-block">(Decision Statistic / OEL) × 100 <span class="equals">=</span> % of OEL</div>
                <ol class="step-list">
                  <li>{{ "System calculates % of OEL from decision statistic and agent OEL" | translate }}</li>
                  <li>{{ "% of OEL matched against Quant thresholds to assign a Likelihood Level" | translate }}</li>
                </ol>
              </div>
              <div class="hiw-section hiw-full-width">
                <h4><span class="material-icons">trending_up</span> {{ "Risk Score Calculation" | translate }}</h4>
                <div class="formula-block">{{ "Likelihood (1–{{likelihoodCount}}) × Consequence (1–{{consequenceCount}}) = Health Risk Score (1–{{maxRiskScore}}) → Risk Level" | translate: {likelihoodCount: likelihoodCount, consequenceCount: consequenceCount, maxRiskScore: maxRiskScore} }}</div>
              </div>
            </div>
          </div>
        </div>
      }
      <!-- Cross-section Validation Warnings -->
      @if (!bayesianOnly && hasDimensionMismatch) {
        <div class="validation-warning">
          <span class="material-icons">warning_amber</span>
          {{ "Dimension mismatch: {{likelihoodCount}} likelihood levels but {{riskCount}} risk levels. The risk matrix rows should match the likelihood level count." | translate: {likelihoodCount: exposureLikelihoodLevels.length, riskCount: riskLevels.length} }}
        </div>
      }
      @if (!bayesianOnly && hasThresholdCoverageGap) {
        <div class="validation-warning">
          <span class="material-icons">warning_amber</span>
          {{ "Risk level thresholds do not cover the full score range (1–{{maxRiskScore}}). Check for gaps in the Score Range column." | translate: {maxRiskScore: maxRiskScore} }}
        </div>
      }
      <!-- ═══ NON-DETECT HANDLING CARD ═══ -->
      <!-- G19/DASH-2977 (FE-3): configures input data, upstream of every scoring card, so it is the
      FIRST section and lives on the standard (non-Bayesian) view — it governs ALL exposure
      statistics, not just the Bayesian ones. -->
      @if (!bayesianOnly) {
        <div class="section-card" id="section-nondetect-handling">
          <div class="section-card-header">
            <div>
              <div class="section-card-title">
                <span class="material-icons">rule</span>
                {{ "Non-Detect Handling" | translate }}
              </div>
            </div>
            <span class="section-card-subtitle">{{ "How non-detect (< LOQ) lab results are treated in every exposure statistic" | translate }}</span>
          </div>
          <div class="config-section nondetect-handling-section">
            <label class="config-label" for="censoredDataMethodSelect">{{ "Non-Detect Handling Method" | translate }}</label>
            <mat-form-field appearance="outline" class="nondetect-method-field">
              <mat-select id="censoredDataMethodSelect" [(ngModel)]="censoredDataMethod" (ngModelChange)="onConfigChanges()">
                @for (option of censoredDataMethodOptions; track trackByIndex($index)) {
                  <mat-option [value]="option.value">
                    {{ option.label | translate }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (censoredDataMethod === censoredDataMethodEnum.ExcludeNonDetects) {
              <div class="info-banner nondetect-exclude-warning">
                <span class="material-icons">warning_amber</span>
                <div>
                  {{ "Excluding non-detects removes low results, so each remaining result carries more weight. A register can therefore become less compliant, not more. This is expected — it is not a bug." | translate }}
                </div>
              </div>
            }
          </div>
        </div>
      }
      <!-- ═══ ASSESSMENT DIMENSIONS CARD ═══ -->
      @if (!bayesianOnly) {
        <div class="section-card" id="section-assessment-dimensions">
          <div class="section-card-header">
            <div>
              <div class="section-card-title">
                <span class="material-icons">straighten</span>
                {{ "Assessment Dimensions" | translate }}
              </div>
            </div>
            <span class="section-card-subtitle">{{ "Configure matrix sizes for qualitative and risk assessments" | translate }}</span>
          </div>
          <div class="assessment-dimensions-row">
            <!-- Qualitative Matrix Size -->
            <div class="dimension-card">
              <label class="dimension-card-title">
                <span class="material-icons">grid_view</span>
                {{ "Qualitative Matrix Size" | translate }}
              </label>
              <div class="dimension-inputs">
                <div class="dimension-group">
                  <label class="small-label">{{ "Exceedance Rows" | translate }}</label>
                  <select [(ngModel)]="matrixExceedanceDimension" (ngModelChange)="onMatrixSizeChange()" class="dimension-select">
                    @for (dim of matrixDimensionOptions; track dim) {
                      <option [ngValue]="dim">{{ dim }}</option>
                    }
                  </select>
                </div>
                <span class="dimension-sep">×</span>
                <div class="dimension-group">
                  <label class="small-label">{{ "Frequency Cols" | translate }}</label>
                  <select [(ngModel)]="matrixFrequencyDimension" (ngModelChange)="onMatrixSizeChange()" class="dimension-select">
                    @for (dim of matrixDimensionOptions; track dim) {
                      <option [ngValue]="dim">{{ dim }}</option>
                    }
                  </select>
                </div>
              </div>
              <div class="matrix-info">
                <span class="material-icons">info_outline</span>
                {{ "Max qualitative score will be {{maxProduct}}" | translate: {maxProduct: maxQualitativeProduct} }}
              </div>
            </div>
            <!-- Connector -->
            <div class="dimension-connector">
              <span class="dimension-connector-label">{{ "feeds into" | translate }}</span>
            </div>
            <!-- Risk Matrix Size -->
            <div class="dimension-card">
              <label class="dimension-card-title">
                <span class="material-icons">grid_on</span>
                {{ "Risk Matrix Size" | translate }}
              </label>
              <div class="dimension-inputs">
                <div class="dimension-group">
                  <label class="small-label">{{ "Likelihood Rows" | translate }}</label>
                  <div class="dimension-computed">{{ likelihoodCount }}</div>
                </div>
                <span class="dimension-sep">×</span>
                <div class="dimension-group">
                  <label class="small-label">{{ "Consequence Cols" | translate }}</label>
                  <div class="dimension-computed">{{ consequenceCount }}</div>
                </div>
              </div>
              <div class="matrix-info">
                <span class="material-icons">info_outline</span>
                {{ "Max risk score:" | translate }} <strong>{{ maxRiskScore }}</strong>
              </div>
            </div>
          </div>
          <!-- Threshold Suggestion Popup -->
          @if (showThresholdSuggestion && thresholdSuggestionPending) {
            <div class="threshold-suggestion">
              <div class="suggestion-header">
                <span class="material-icons">lightbulb</span>
                {{ "Auto-suggest Thresholds" | translate }}
              </div>
              <div class="suggestion-content">
                <p>{{ "Based on the new matrix size ({{maxProduct}}), here are the suggested threshold ranges:" | translate: {maxProduct: maxQualitativeProduct} }}</p>
                <div class="suggestion-table">
                  <div class="suggestion-header-row">
                    <div class="suggestion-col-name">{{ "Level" | translate }}</div>
                    <div class="suggestion-col-current">{{ "Current" | translate }}</div>
                    <div class="suggestion-col-arrow">→</div>
                    <div class="suggestion-col-suggested">{{ "Suggested" | translate }}</div>
                  </div>
                  @for (suggestion of proposedQualThresholds; track suggestion) {
                    <div class="suggestion-data-row">
                      <div class="suggestion-col-name">
                        <div class="color-dot" [style.background]="suggestion.color"></div>
                        {{ suggestion.levelName }}
                      </div>
                      <div class="suggestion-col-current">{{ suggestion.currentMin }}–{{ suggestion.currentMax }}</div>
                      <div class="suggestion-col-arrow">→</div>
                      <div class="suggestion-col-suggested">
                        <strong>{{ suggestion.suggestedMin }}–{{ suggestion.suggestedMax }}</strong>
                      </div>
                    </div>
                  }
                </div>
                <div class="suggestion-actions">
                  <button class="btn btn-primary" (click)="applySuggestedThresholds()">
                    <span class="material-icons">check</span> {{ "Apply Suggestions" | translate }}
                  </button>
                  <button class="btn btn-outline" (click)="dismissThresholdSuggestion()">
                    <span class="material-icons">close</span> {{ "Keep Manual Settings" | translate }}
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }
      <!-- ═══ QUALITATIVE MATRIX CONFIGURATION CARD ═══ -->
      @if (!bayesianOnly) {
        <div class="section-card" id="section-qualitative-matrix">
          <div class="section-card-header">
            <div>
              <div class="section-card-title">
                <span class="material-icons">grid_3x3</span>
                {{ "Qualitative Risk Matrix Configuration" | translate }}
              </div>
            </div>
            <span class="section-card-subtitle">{{ "Configure a 2D matrix for qualitative risk assessments" | translate }}</span>
          </div>
          <!-- Info Banner -->
          <div class="info-banner">
            <span class="material-icons">info</span>
            <div>
              <strong>{{ "Qualitative Matrix Overview" | translate }}</strong>
              {{ "The qualitative matrix allows assessors to rate two dimensions (e.g., Exceedance Probability and Frequency) and their product maps to a likelihood score and classification." | translate }}
              <ul>
                <li><strong>{{ "Dimension 1:" | translate }}</strong> {{ "First dimension (e.g., Exceedance Probability: 1-5)" | translate }}</li>
                <li><strong>{{ "Dimension 2:" | translate }}</strong> {{ "Second dimension (e.g., Frequency of Exposure: 1-5)" | translate }}</li>
                <li><strong>{{ "Product Score:" | translate }}</strong> {{ "Dimension 1 × Dimension 2 (e.g., 1-25 for 5×5)" | translate }}</li>
                <li><strong>{{ "Likelihood Mapping:" | translate }}</strong> {{ "Maps product score ranges to likelihood scores and labels" | translate }}</li>
              </ul>
            </div>
          </div>
          <!-- Dimensions Editor -->
          <div class="config-section">
            <label class="config-label">{{ "Dimensions Configuration" | translate }}</label>
            <div class="dimensions-side-by-side">
              @for (dimension of qualitativeMatrixConfig.dimensions; track dimension; let dimIndex = $index) {
                <div
                  class="dimension-editor">
                  <div class="dimension-header">
                    <input class="dimension-name-input" type="text"
                      [(ngModel)]="dimension.name"
                      (ngModelChange)="onConfigChanges()"
                      placeholder="{{ 'Dimension Name' | translate }}" />
                      <span class="dimension-count"
                        [class.dimension-count-error]="dimension.options.length !== dimensionRequiredCounts[dimIndex]">
                        ({{ dimension.options.length }}/{{ dimensionRequiredCounts[dimIndex] }} {{ "options" | translate }})
                      </span>
                    </div>
                    @if (dimension.options.length !== dimensionRequiredCounts[dimIndex]) {
                      <div class="dimension-count-warning"
                        >
                        <span class="material-icons">warning</span>
                        {{ "Requires exactly {{required}} options to match the configured matrix size." | translate: { required: dimensionRequiredCounts[dimIndex] } }}
                      </div>
                    }
                    <input class="inline-input" type="text" style="width:100%;margin-bottom:8px"
                      [(ngModel)]="dimension.description"
                      (ngModelChange)="onConfigChanges()"
                      placeholder="{{ 'Dimension description (shown as tooltip)' | translate }}" />
                      <div class="dimension-options">
                        @for (option of dimension.options; track option; let optIndex = $index) {
                          <div
                            class="option-row">
                            <input class="option-value-input" type="number" step="1"
                              #dimensionOptionValueInput
                              [value]="option.value"
                              (change)="updateDimensionOptionValue(dimension.id, option.value, +dimensionOptionValueInput.value)"
                              placeholder="{{ 'Value' | translate }}" />
                              <input class="option-label-input" type="text" noSanitize
                                [(ngModel)]="option.label"
                                (ngModelChange)="onConfigChanges()"
                                placeholder="{{ 'Label' | translate }}" />
                                <input class="option-label-input" type="text"
                                  [(ngModel)]="option.description"
                                  (ngModelChange)="onConfigChanges()"
                                  placeholder="{{ 'Description' | translate }}" />
                                  <button class="btn-icon delete"
                                    (click)="removeDimensionOption(dimension.id, option.value)"
                                    [disabled]="dimension.options.length <= 2">
                                    <span class="material-icons">delete_outline</span>
                                  </button>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <!-- Qual Score Banding Bar -->
                    @if (exposureLikelihoodLevels.length) {
                      <div class="preview-section">
                        <div class="preview-label">{{ "Qualitative Score Scale (Exceedance × Frequency = {{maxProduct}})" | translate: {maxProduct: maxQualitativeProduct} }}</div>
                        <div class="preview-bar">
                          @for (seg of getLikelihoodQualPreviewSegments(); track trackByIndex($index)) {
                            <div class="preview-segment"
                              [style.background]="seg.color" [style.width.%]="seg.widthPct">
                              <span class="segment-label">{{ seg.name }}</span>
                            </div>
                          }
                        </div>
                        <app-risk-config-legend [items]="likelihoodQualLegendItems"></app-risk-config-legend>
                      </div>
                    }
                    <!-- Matrix Preview -->
                    @if (qualitativeMatrixConfig.dimensions[0]?.options.length) {
                      <div class="matrix-preview-section">
                        <div class="preview-label">{{ "Qualitative Matrix Preview" | translate }}</div>
                        <table class="qual-matrix">
                          <thead>
                            <tr>
                              <th></th>
                              <th class="axis-title" [attr.colspan]="getAxisLabels($safeNavigationMigration(qualitativeMatrixConfig.dimensions[1]?.options), 1).length">
                                {{ qualitativeMatrixConfig.dimensions[1]?.name }}
                              </th>
                            </tr>
                            <tr>
                              <th class="axis-title row-title">{{ qualitativeMatrixConfig.dimensions[0]?.name }}</th>
                              @for (opt of getAxisLabels($safeNavigationMigration(qualitativeMatrixConfig.dimensions[1]?.options), 1); track trackByIndex($index)) {
                                <th>
                                  {{ opt.value }}
                                </th>
                              }
                            </tr>
                          </thead>
                          <tbody>
                            @for (row of getQualitativeMatrixPreview(); track trackByIndex($index); let rowIndex = $index) {
                              <tr>
                                <td class="row-header">{{ getAxisLabels($safeNavigationMigration(qualitativeMatrixConfig.dimensions[0]?.options), 0)[rowIndex]?.value }}</td>
                                @for (cell of row; track trackByIndex($index)) {
                                  <td
                                    class="qual-cell"
                                    [style.background]="cell.color"
                                    [matTooltip]="getMatrixCellTooltip(cell)">
                                    <span class="cell-value">{{ cell.product }}</span>
                                    <span class="cell-label">{{ cell.mapping?.likelihoodScore || '?' }}</span>
                                  </td>
                                }
                              </tr>
                            }
                          </tbody>
                        </table>
                        <!-- DASH-2278 #3: Removed the duplicate "Likelihood Mappings" table here.
                        The comprehensive Exposure Likelihood Levels table below is the single
                        source of truth (includes both qualitative & quantitative thresholds). -->
                      </div>
                    }
                  </div>
                }
                <!-- ═══ EXPOSURE LIKELIHOOD LEVELS CARD ═══ -->
                @if (!bayesianOnly) {
                  <div class="section-card" id="section-likelihood-levels">
                    <div class="section-card-header">
                      <div>
                        <div class="section-card-title">
                          <span class="material-icons">analytics</span>
                          {{ "Exposure Likelihood Levels" | translate }}
                        </div>
                      </div>
                      <span class="section-card-subtitle">{{ "Defines how exposure measurements map to likelihood scores (1–{{count}})" | translate: {count: likelihoodCount} }}</span>
                    </div>
                    <!-- Info Banner -->
                    <div class="info-banner">
                      <span class="material-icons">info</span>
                      <div>
                        <strong>{{ "Likelihood is determined two ways:" | translate }}</strong>
                        <ul>
                          <li><strong>{{ "Qualitative" | translate }}</strong> — {{ "Assessor rates EP × Frequency. Product (1–{{maxProduct}}) matched against Qual Thresholds." | translate: {maxProduct: maxQualitativeProduct} }}</li>
                          <li><strong>{{ "Quantitative" | translate }}</strong> — {{ "System calculates" | translate }}
                          <span class="formula">% of OEL = (Decision Statistic / OEL) × 100</span>
                          {{ "matched against Quant %OEL thresholds." | translate }}
                        </li>
                      </ul>
                      {{ "The resulting Likelihood Score" | translate }} (1–{{ likelihoodCount }}) {{ "feeds into the Health Risk Score formula below." | translate }}
                    </div>
                  </div>
                  <!-- Decision statistic note -->
                  <div class="decision-stat-note">
                    <span class="material-icons">info_outline</span>
                    {{ "Decision statistic:" | translate }} <strong>&nbsp;{{ "95th Percentile at 70% UCL" | translate }}&nbsp;</strong> {{ "(configured in Tenant Settings)" | translate }}
                  </div>
                  <!-- DASH-3163: the Min Samp column only governs when EN 689 is disabled. Flag it so nobody
                  tunes values that are being ignored. -->
                  @if (useEN689) {
                    <div class="info-banner min-samp-inactive-banner">
                      <span class="material-icons">info</span>
                      <div>{{ "EN 689 Compliance Testing is enabled, so the Min Samp values below are not used. Disable EN 689 for this jurisdiction to make them govern the required sample count." | translate }}</div>
                    </div>
                  }
                  <!-- Likelihood Table -->
                  <div class="config-table-wrap">
                    <table class="config-table">
                      <thead>
                        <tr>
                          <th style="width:50px">{{ "Factor" | translate }}</th>
                          <th style="width:130px">{{ "Name" | translate }}</th>
                          <th style="width:150px">{{ "Quantitative Name" | translate }}</th>
                          <th colspan="2">{{ "Qual Thresholds" | translate }}</th>
                          <th colspan="2">{{ "Quant %OEL" | translate }}</th>
                          <th style="width:70px" [matTooltip]="minSampColumnTooltip | translate">
                            {{ "Min Samp" | translate }}
                            <span class="material-icons" style="font-size:14px;vertical-align:middle;opacity:0.6">info_outline</span>
                          </th>
                          <th style="width:120px">{{ "Color" | translate }}</th>
                          <th style="width:100px" [matTooltip]="reassessFrequencyTooltip | translate">
                            {{ "Reassess" | translate }}
                            <span class="material-icons" style="font-size:14px;vertical-align:middle;opacity:0.6">info_outline</span>
                          </th>
                          <th style="width:200px">{{ "Description" | translate }}</th>
                          <th style="width:50px"></th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (level of exposureLikelihoodLevels; track trackByIndex($index); let i = $index) {
                          <tr>
                            <td class="key-cell">
                              <input #likelihoodKeyInput class="inline-input narrow key-input" type="number" min="1" step="1"
                                [value]="level.key"
                                (change)="updateExposureLevelKey(i, likelihoodKeyInput)" />
                              </td>
                              <td>
                                <div class="name-with-indicator">
                                  <div class="color-indicator" [style.background]="level.Color"></div>
                                  <input class="inline-input name-input" type="text" style="width:120px"
                                    [(ngModel)]="level.Name" (ngModelChange)="onConfigChanges()" />
                                  </div>
                                </td>
                                <td>
                                  <input class="inline-input" type="text" style="width:140px"
                                    [(ngModel)]="level.QuantitativeName" (ngModelChange)="onConfigChanges()" />
                                  </td>
                                  <td>
                                    <div class="threshold-group">
                                      <input class="inline-input narrow" type="number" min="1" step="1"
                                        [(ngModel)]="level.minQualThreshold" (ngModelChange)="onConfigChanges()" />
                                        <span class="threshold-sep">—</span>
                                        <input class="inline-input narrow" type="number" min="1" step="1"
                                          [(ngModel)]="level.maxQualThreshold" (ngModelChange)="onConfigChanges()" />
                                        </div>
                                      </td>
                                      <td></td>
                                      <td>
                                        <div class="threshold-group">
                                          <input class="inline-input narrow" type="number" style="width:70px"
                                            [(ngModel)]="level.minQuantThreshold" (ngModelChange)="onConfigChanges()" />
                                            <span class="threshold-sep">—</span>
                                            <input class="inline-input narrow" type="number" style="width:70px"
                                              [(ngModel)]="level.maxQuantThreshold" (ngModelChange)="onConfigChanges()" />
                                              <span class="unit-label">%</span>
                                            </div>
                                          </td>
                                          <td></td>
                                          <td>
                                            <input class="inline-input narrow" type="number" min="1" max="1000"
                                              [class.input-inactive]="useEN689" [disabled]="useEN689 || !canEditRiskConfig"
                                              [matTooltip]="useEN689 ? ('Not used while EN 689 Compliance Testing is enabled.' | translate) : ''"
                                              [(ngModel)]="level.MinSamples" (ngModelChange)="onConfigChanges()" />
                                            </td>
                                            <td>
                                              <div class="color-cell">
                                                <div class="color-swatch" [style.background]="level.Color">
                                                  <input #likelihoodColorInput type="color" [value]="getColorHex(level.Color)"
                                                    (input)="onLikelihoodColorSwatchChange(level, likelihoodColorInput.value)" />
                                                  </div>
                                                  <input #likelihoodColorHexInput class="color-hex" [value]="level.Color"
                                                    (change)="onLikelihoodColorHexChange(level, likelihoodColorHexInput.value)" />
                                                  </div>
                                                </td>
                                                <td>
                                                  <div class="reassess-cell">
                                                    <input class="inline-input narrow" type="number" min="1"
                                                      [(ngModel)]="level.ReassessmentFrequencyMonths" (ngModelChange)="onConfigChanges()" />
                                                      <span class="unit-label">mo</span>
                                                    </div>
                                                  </td>
                                                  <td>
                                                    <input class="inline-input" type="text" style="width:100%"
                                                      [(ngModel)]="level.Description" (ngModelChange)="onConfigChanges()"
                                                      placeholder="{{ 'Optional description' | translate }}" />
                                                    </td>
                                                    <td>
                                                      <div class="row-actions">
                                                        <button class="btn-icon delete" (click)="deleteExposureLevel(i)"
                                                          [disabled]="exposureLikelihoodLevels.length <= 1">
                                                          <span class="material-icons">delete_outline</span>
                                                        </button>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                }
                                              </tbody>
                                            </table>
                                            <div class="add-row">
                                              <button class="btn btn-text" (click)="addExposureLevel()">
                                                <span class="material-icons">add</span> {{ "Add Likelihood Level" | translate }}
                                              </button>
                                            </div>
                                          </div>
                                          <!-- Likelihood Previews -->
                                          @if (exposureLikelihoodLevels.length) {
                                            <div class="preview-section">
                                              <div class="preview-row">
                                                <div class="preview-col">
                                                  <div class="preview-label">{{ "Qualitative Score Scale (Exceedance × Frequency = {{maxProduct}})" | translate: {maxProduct: maxQualitativeProduct} }}</div>
                                                  <div class="preview-bar">
                                                    @for (seg of getLikelihoodQualPreviewSegments(); track trackByIndex($index)) {
                                                      <div class="preview-segment"
                                                        [style.background]="seg.color" [style.width.%]="seg.widthPct">
                                                        <span class="segment-label">{{ seg.name }}</span>
                                                      </div>
                                                    }
                                                  </div>
                                                  <app-risk-config-legend [items]="likelihoodQualLegendItems"></app-risk-config-legend>
                                                </div>
                                                <div class="preview-col">
                                                  <div class="preview-label">{{ "Quantitative % of OEL Scale" | translate }}</div>
                                                  <div class="preview-bar">
                                                    @for (seg of getLikelihoodQuantPreviewSegments(); track trackByIndex($index)) {
                                                      <div class="preview-segment"
                                                        [style.background]="seg.color" [style.width.%]="seg.widthPct">
                                                        <span class="segment-label">{{ seg.name }}</span>
                                                      </div>
                                                    }
                                                  </div>
                                                  <div class="oel-marker-container">
                                                    <div class="oel-marker" [style.left.%]="getOelMarkerPosition()">
                                                      <span class="oel-marker-label">OEL 100%</span>
                                                    </div>
                                                  </div>
                                                  <app-risk-config-legend [items]="likelihoodQuantLegendItems"></app-risk-config-legend>
                                                </div>
                                              </div>
                                            </div>
                                          }
                                        </div>
                                      }
                                      @if (bayesianOnly) {
                                        <div class="section-card" id="section-bayesian-config">
                                          <div class="section-card-header">
                                            <div>
                                              <div class="section-card-title">
                                                <span class="material-icons">insights</span>
                                                {{ "Bayesian Configuration" | translate }}
                                              </div>
                                            </div>
                                            <span class="section-card-subtitle">{{ "Configure jurisdiction-level defaults for Bayesian Analysis" | translate }}</span>
                                          </div>
                                          <div class="info-banner">
                                            <span class="material-icons">info</span>
                                            <div>
                                              <strong>{{ "Bayesian defaults are inherited by risk assessments" | translate }}</strong>
                                              {{ "Assessments can override the active prior while preserving an audit note in the Bayesian Analysis tab." | translate }}
                                            </div>
                                          </div>
                                          <div class="config-table-wrap bayesian-config-grid">
                                            <div class="bayesian-config-item bayesian-toggle-item">
                                              <label>{{ "Bayesian Analysis Enabled" | translate }}</label>
                                              <label class="toggle">
                                                <input type="checkbox" [(ngModel)]="bayesianEnabled" (ngModelChange)="onConfigChanges()" />
                                                <span class="slider"></span>
                                              </label>
                                            </div>
                                            <div class="bayesian-config-item">
                                              <label>{{ "Framework" | translate }}</label>
                                              <input class="inline-input" type="text"
                                                [(ngModel)]="bayesianFramework"
                                                (ngModelChange)="onConfigChanges()"
                                                placeholder="{{ 'e.g., Bayesian Exposure Assessment' | translate }}" />
                                              </div>
                                              <div class="bayesian-config-item">
                                                <label>{{ "Informative Prior Source" | translate }}</label>
                                                <input class="inline-input" type="text"
                                                  [(ngModel)]="bayesianInformativePriorSource"
                                                  (ngModelChange)="onConfigChanges()"
                                                  [disabled]="bayesianInformativePriorDisabled"
                                                  placeholder="{{ 'e.g., QRA' | translate }}" />
                                                </div>
                                                <div class="bayesian-config-item bayesian-toggle-item">
                                                  <label>
                                                    {{ "Default Active Prior" | translate }}
                                                    @if (bayesianInformativePriorDisabled) {
                                                      <span class="coming-soon-chip">{{ 'Informative (QRA) — Coming soon' | translate }}</span>
                                                    }
                                                  </label>
                                                  <mat-radio-group [ngModel]="bayesianDefaultPriorMode" class="d-flex align-items-center bayesian-prior-radio-group">
                                                    <mat-radio-button [value]="0" (change)="onBayesianDefaultPriorModeChange(0)" class="mr-3">
                                                      {{ 'Uninformative' | translate }}
                                                    </mat-radio-button>
                                                    <mat-radio-button [value]="1" (change)="onBayesianDefaultPriorModeChange(1)" [disabled]="bayesianInformativePriorDisabled">
                                                      {{ 'Informative (QRA)' | translate }}
                                                    </mat-radio-button>
                                                  </mat-radio-group>
                                                </div>
                                                <div class="bayesian-config-item bayesian-toggle-item">
                                                  <label>
                                                    {{ "Default Prior Width" | translate }}
                                                    @if (bayesianNarrowPriorDisabled) {
                                                      <span class="coming-soon-chip">{{ 'Narrow — Coming soon' | translate }}</span>
                                                    }
                                                  </label>
                                                  <mat-radio-group [ngModel]="bayesianDefaultPriorWidth" class="d-flex align-items-center bayesian-prior-radio-group">
                                                    <mat-radio-button value="wide" (change)="bayesianDefaultPriorWidth = 'wide'; onConfigChanges()" class="mr-3">
                                                      {{ 'Wide Prior' | translate }}
                                                    </mat-radio-button>
                                                    <mat-radio-button value="narrow" (change)="bayesianDefaultPriorWidth = 'narrow'; onConfigChanges()" [disabled]="bayesianNarrowPriorDisabled">
                                                      {{ 'Narrow' | translate }}
                                                    </mat-radio-button>
                                                  </mat-radio-group>
                                                </div>
                                                <div class="bayesian-config-item bayesian-toggle-item">
                                                  <label>{{ "Enable Censoring Guardrail" | translate }}</label>
                                                  <label class="toggle">
                                                    <input type="checkbox" [(ngModel)]="bayesianEnableCensoringGuardrail" (ngModelChange)="onConfigChanges()" />
                                                    <span class="slider"></span>
                                                  </label>
                                                </div>
                                                <div class="bayesian-config-item bayesian-config-notes">
                                                  <label>{{ "Configuration Notes" | translate }}</label>
                                                  <textarea class="inline-input" rows="3"
                                                    [(ngModel)]="bayesianNotes"
                                                    (ngModelChange)="onConfigChanges()"
                                                  placeholder="{{ 'Optional notes shown to assessors when Bayesian defaults are applied.' | translate }}"></textarea>
                                                </div>
                                                <div class="bayesian-config-item bayesian-config-notes">
                                                  <label>{{ "Read-only Reference (% OEL Bands)" | translate }}</label>
                                                  <div class="bayesian-readonly-reference">
                                                    @for (band of bayesianQuantBandsReference; track trackByIndex($index)) {
                                                      <span class="band-ref-item">
                                                        <span class="band-ref-swatch" [style.background-color]="band.color"></span>
                                                        <span>{{ band.name }} · {{ band.range }}</span>
                                                      </span>
                                                    }
                                                    @if (!bayesianQuantBandsReference.length) {
                                                      <span class="band-ref-empty">-</span>
                                                    }
                                                  </div>
                                                  <small>{{ "This reference is inherited from Exposure Likelihood Levels and cannot be edited in this section." | translate }}</small>
                                                </div>
                                              </div>
                                            </div>
                                          }
                                          <!-- ═══ CONSEQUENCE RATINGS CARD ═══ -->
                                          @if (!bayesianOnly) {
                                            <div class="section-card" id="section-consequence-ratings">
                                              <div class="section-card-header">
                                                <div>
                                                  <div class="section-card-title">
                                                    <span class="material-icons">grade</span>
                                                    {{ "Consequence Ratings" | translate }}
                                                  </div>
                                                </div>
                                                <span class="section-card-subtitle">{{ "Define consequence rating options for risk assessments" | translate }}</span>
                                              </div>
                                              <!-- Info Banner -->
                                              <div class="info-banner">
                                                <span class="material-icons">info</span>
                                                <div>
                                                  <strong>{{ "Consequence Ratings" | translate }}</strong>
                                                  {{ "Configure custom consequence rating scales (non-negative integers). If no custom ratings are defined, the system defaults to a 0-10 scale." | translate }}
                                                </div>
                                              </div>
                                              <!-- Consequence Ratings Table -->
                                              <div class="config-table-wrap">
                                                <table class="config-table">
                                                  <thead>
                                                    <tr>
                                                      <th style="width:80px">{{ "Value" | translate }}</th>
                                                      <th style="width:150px">{{ "Label" | translate }}</th>
                                                      <th style="width:120px">{{ "Req MOA" | translate }}</th>
                                                      <th>{{ "Description" | translate }}</th>
                                                      <th style="width:50px"></th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    @for (rating of consequenceRatings; track trackByIndex($index); let i = $index) {
                                                      <tr>
                                                        <td>
                                                          <input class="inline-input narrow" type="number" min="0" step="1"
                                                            [(ngModel)]="rating.value" (ngModelChange)="validateConsequenceRatingValue(i)" />
                                                          </td>
                                                          <td>
                                                            <input class="inline-input" type="text" style="width:100%"
                                                              [(ngModel)]="rating.label" (ngModelChange)="onConfigChanges()"
                                                              placeholder="{{ 'e.g., Low, Medium, High' | translate }}" />
                                                            </td>
                                                            <td>
                                                              <div class="select-cell">
                                                                <select class="inline-select"
                                                                  [(ngModel)]="rating.reqMethodOfAssessment"
                                                                  (ngModelChange)="onConfigChanges()">
                                                                  <option [ngValue]="0">{{ "Qualitative" | translate }}</option>
                                                                  <option [ngValue]="1">{{ "Quantitative" | translate }}</option>
                                                                  <option [ngValue]="2">{{ "N/A" | translate }}</option>
                                                                </select>
                                                              </div>
                                                            </td>
                                                            <td>
                                                              <input class="inline-input" type="text" style="width:100%"
                                                                [(ngModel)]="rating.description" (ngModelChange)="onConfigChanges()"
                                                                placeholder="{{ 'Optional description' | translate }}" />
                                                              </td>
                                                              <td>
                                                                <div class="row-actions">
                                                                  <button class="btn-icon delete" (click)="deleteConsequenceRating(i)"
                                                                    [disabled]="consequenceRatings.length <= 1">
                                                                    <span class="material-icons">delete_outline</span>
                                                                  </button>
                                                                </div>
                                                              </td>
                                                            </tr>
                                                          }
                                                        </tbody>
                                                      </table>
                                                      <div class="add-row">
                                                        <button class="btn btn-text" (click)="addConsequenceRating()">
                                                          <span class="material-icons">add</span> {{ "Add Consequence Rating" | translate }}
                                                        </button>
                                                      </div>
                                                    </div>
                                                  </div>
                                                }
                                                <!-- ═══ RISK LEVELS CARD ═══ -->
                                                @if (!bayesianOnly) {
                                                  <div class="section-card" id="section-risk-levels">
                                                    <div class="section-card-header">
                                                      <div>
                                                        <div class="section-card-title">
                                                          <span class="material-icons">warning</span>
                                                          {{ "Risk Levels" | translate }}
                                                        </div>
                                                      </div>
                                                      <span class="section-card-subtitle">{{ "Defines how Health Risk Scores map to risk categories" | translate }}</span>
                                                    </div>
                                                    <!-- Info Banner -->
                                                    <div class="info-banner">
                                                      <span class="material-icons">info</span>
                                                      <div>
                                                        <strong>{{ "How Risk Score is calculated:" | translate }}</strong>
                                                        {{ "Each SEG + Agent pair in a risk register gets a Health Risk Score using the formula" | translate }}
                                                        <span class="formula">{{ "Health Risk Score = Likelihood Score × Consequence Rating" | translate }}</span>
                                                        <ul>
                                                          <li><strong>{{ "Likelihood Score" | translate }}</strong> ({{ minLikelihoodValue }}–{{ maxLikelihoodValue }}) — {{ "determined by the Exposure Likelihood Levels above" | translate }}</li>
                                                          <li><strong>{{ "Consequence Rating" | translate }}</strong> ({{ minConsequenceValue }}–{{ maxConsequenceValue }}) — {{ "set on the Agent based on its health hazard severity" | translate }}</li>
                                                        </ul>
                                                        {{ "The resulting score" | translate }} (1–{{ maxRiskScore }}) {{ "falls into the Threshold bands below." | translate }}
                                                        <p class="cmr-note"><strong>{{ "Note:" | translate }}</strong> {{ "Any carcinogen, mutagen, or reproductive toxin (CMR) must proceed through quantitative assessment regardless of qualitative risk rating." | translate }}</p>
                                                      </div>
                                                    </div>
                                                    <!-- Collapsible What Each Setting Controls -->
                                                    <div class="hiw-toggle" (click)="showRiskHowItWorks = !showRiskHowItWorks">
                                                      <span class="material-icons">{{ showRiskHowItWorks ? 'expand_less' : 'expand_more' }}</span>
                                                      {{ "Risk Matrix & What each setting controls" | translate }}
                                                    </div>
                                                    <div class="hiw-content" [class.open]="showRiskHowItWorks">
                                                      <div class="hiw-inner">
                                                        <div class="hiw-grid">
                                                          <div class="hiw-section">
                                                            <h4><span class="material-icons">grid_on</span> {{ likelihoodCount }}×{{ consequenceCount }} {{ "Risk Matrix" | translate }}</h4>
                                                            <p>{{ "Each cell shows the Health Risk Score. Colors are determined by the threshold bands." | translate }}</p>
                                                            <table class="risk-matrix">
                                                              <tr>
                                                                <th style="width:90px"></th>
                                                                <th class="axis-title" [attr.colspan]="consequenceAxisCache.length">{{ "Consequence Rating" | translate }}</th>
                                                              </tr>
                                                              <tr>
                                                                <th style="width:90px" class="axis-title row-title">{{ "Likelihood" | translate }}</th>
                                                                @for (c of consequenceAxisCache; track trackByIndex($index)) {
                                                                  <th>{{ c.label }}</th>
                                                                }
                                                              </tr>
                                                              @for (row of riskMatrixCache; track trackByIndex($index)) {
                                                                <tr>
                                                                  <td class="row-header">{{ row.likelihoodLabel }}</td>
                                                                  @for (cell of row.cells; track trackByIndex($index)) {
                                                                    <td [style.background]="cell.color" style="font-weight:700;">{{ cell.score }}</td>
                                                                  }
                                                                </tr>
                                                              }
                                                            </table>
                                                            <p class="matrix-note">{{ "Scores range from" | translate }} {{ minRiskScore }} ({{ minLikelihoodValue }}×{{ minConsequenceValue }}) {{ "to" | translate }} {{ maxRiskScore }} ({{ maxLikelihoodValue }}×{{ maxConsequenceValue }}).</p>
                                                          </div>
                                                          <div class="hiw-section">
                                                            <h4><span class="material-icons">settings</span> {{ "What each setting controls" | translate }}</h4>
                                                            <ol class="step-list">
                                                              <li><strong>{{ "Thresholds (Min–Max)" | translate }}</strong> — {{ "Score range for this risk level. Ranges must be contiguous." | translate }}</li>
                                                              <li><strong>{{ "Color" | translate }}</strong> — {{ "Displayed on risk register, reports, and sample result cards." | translate }}</li>
                                                              <li><strong>{{ "Req MOA" | translate }}</strong> — {{ "Required Method of Assessment: Qualitative (professional judgement), Quantitative (air sampling + lab analysis), or N/A (does not apply)." | translate }}</li>
                                                              <li><strong>{{ "Control Adequacy" | translate }}</strong> — {{ "ON = existing controls adequate. OFF = flags for improvement." | translate }}</li>
                                                              <li><strong>{{ "Post-Assessment Control Adequacy" | translate }}</strong> — {{ "Same as above, evaluated after residual risk is recalculated with applied controls." | translate }}</li>
                                                            </ol>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <!-- Risk Matrix Preview (above table) -->
                                                    @if (riskLevels.length) {
                                                      <div class="preview-section risk-matrix-full-preview">
                                                        <div class="preview-label">{{ "Risk Score Scale Preview" | translate }}</div>
                                                        <div class="preview-bar">
                                                          @for (seg of getRiskPreviewSegments(); track trackByIndex($index)) {
                                                            <div class="preview-segment"
                                                              [style.background]="seg.color" [style.width.%]="seg.widthPct">
                                                              <span class="segment-label">{{ seg.name }}</span>
                                                            </div>
                                                          }
                                                        </div>
                                                        <div class="preview-ticks">
                                                          <span>1</span><span>5</span><span>10</span><span>15</span><span>20</span><span>25</span>
                                                        </div>
                                                        <app-risk-config-legend [items]="riskLevelLegendItems"></app-risk-config-legend>
                                                      </div>
                                                    }
                                                    <!-- Risk Table -->
                                                    <div class="config-table-wrap">
                                                      <table class="config-table">
                                                        <thead>
                                                          <tr>
                                                            <th style="width:50px">{{ "Key" | translate }}</th>
                                                            <th style="width:150px">{{ "Name" | translate }}</th>
                                                            <th colspan="2">{{ "Score Range" | translate }}</th>
                                                            <th style="width:150px">{{ "Color" | translate }}</th>
                                                            <th style="width:120px">{{ "Req MOA" | translate }}</th>
                                                            <th style="width:100px">{{ "Ctrl Adeq" | translate }}</th>
                                                            <th style="width:100px">{{ "Post Ctrl" | translate }}</th>
                                                            <th style="width:200px">{{ "Description" | translate }}</th>
                                                            <th style="width:50px"></th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          @for (level of riskLevels; track trackByIndex($index); let i = $index) {
                                                            <tr>
                                                              <td class="key-cell">
                                                                <input #riskLevelKeyInput class="inline-input narrow key-input" type="number" min="1" step="1"
                                                                  [value]="level.key"
                                                                  (change)="updateRiskLevelKey(i, riskLevelKeyInput)" />
                                                                </td>
                                                                <td>
                                                                  <div class="name-with-indicator">
                                                                    <div class="color-indicator" [style.background]="level.Color"></div>
                                                                    <input class="inline-input name-input" type="text"
                                                                      [(ngModel)]="level.Name" (ngModelChange)="onConfigChanges()" />
                                                                    </div>
                                                                  </td>
                                                                  <td>
                                                                    <div class="threshold-group">
                                                                      <input class="inline-input narrow" type="number"
                                                                        [(ngModel)]="level.minThresholds" (ngModelChange)="onConfigChanges()" />
                                                                        <span class="threshold-sep">—</span>
                                                                        <input class="inline-input narrow" type="number"
                                                                          [(ngModel)]="level.maxThresholds" (ngModelChange)="onConfigChanges()" />
                                                                        </div>
                                                                      </td>
                                                                      <td></td>
                                                                      <td>
                                                                        <div class="color-cell">
                                                                          <div class="color-swatch" [style.background]="level.Color">
                                                                            <input #riskColorInput type="color" [value]="getColorHex(level.Color)"
                                                                              (input)="onColorSwatchChange(level, riskColorInput.value)" />
                                                                            </div>
                                                                            <input #riskColorHexInput class="color-hex" [value]="level.Color"
                                                                              (change)="onColorHexChange(level, riskColorHexInput.value)" />
                                                                            </div>
                                                                          </td>
                                                                          <td>
                                                                            <div class="select-cell">
                                                                              <select class="inline-select"
                                                                                [ngModel]="level.ReqMethodOfAssessment"
                                                                                (ngModelChange)="level.ReqMethodOfAssessment = $event; onConfigChanges()">
                                                                                <option [ngValue]="0">{{ "Qualitative" | translate }}</option>
                                                                                <option [ngValue]="1">{{ "Quantitative" | translate }}</option>
                                                                                <option [ngValue]="2">{{ "N/A" | translate }}</option>
                                                                              </select>
                                                                            </div>
                                                                          </td>
                                                                          <td>
                                                                            <div class="toggle-cell">
                                                                              <label class="toggle">
                                                                                <input #controlAdequacyInput type="checkbox" [checked]="level.ControlAdequacy"
                                                                                  (change)="toggleRiskLevelControlAdequacy(level, controlAdequacyInput.checked)" />
                                                                                  <span class="slider"></span>
                                                                                </label>
                                                                              </div>
                                                                            </td>
                                                                            <td>
                                                                              <div class="toggle-cell">
                                                                                <label class="toggle">
                                                                                  <input #postControlAdequacyInput type="checkbox" [checked]="level.PostAssessmentControlAdequacy"
                                                                                    (change)="toggleRiskLevelPostAssessmentControlAdequacy(level, postControlAdequacyInput.checked)" />
                                                                                    <span class="slider"></span>
                                                                                  </label>
                                                                                </div>
                                                                              </td>
                                                                              <td>
                                                                                <input class="inline-input" type="text" style="width:100%"
                                                                                  [(ngModel)]="level.Description" (ngModelChange)="onConfigChanges()"
                                                                                  placeholder="{{ 'Optional description' | translate }}" />
                                                                                </td>
                                                                                <td>
                                                                                  <div class="row-actions">
                                                                                    <button class="btn-icon delete" (click)="deleteRiskLevel(i)"
                                                                                      [disabled]="riskLevels.length <= 1">
                                                                                      <span class="material-icons">delete_outline</span>
                                                                                    </button>
                                                                                  </div>
                                                                                </td>
                                                                              </tr>
                                                                            }
                                                                          </tbody>
                                                                        </table>
                                                                        <div class="add-row">
                                                                          <button class="btn btn-text" (click)="addRiskLevel()">
                                                                            <span class="material-icons">add</span> {{ "Add Risk Level" | translate }}
                                                                          </button>
                                                                        </div>
                                                                      </div>
                                                                    </div>
                                                                  }
                                                                  <!-- Sticky Save Footer -->
                                                                  @if (hasChanges) {
                                                                    <div class="save-footer">
                                                                      <div class="save-footer-left">
                                                                        <span class="material-icons">edit_note</span>
                                                                        {{ "Unsaved changes" | translate }}
                                                                      </div>
                                                                      <div class="save-footer-right">
                                                                        <button class="btn btn-outline" (click)="discardChanges()">
                                                                          <span class="material-icons">close</span> {{ "Discard" | translate }}
                                                                        </button>
                                                                        <button class="btn btn-primary" (click)="saveConfiguration()"
                                                                          *objectHasPermission="riskConfigPermissionObject; action: updateAction">
                                                                          <span class="material-icons">save</span> {{ "Save Configuration" | translate }}
                                                                        </button>
                                                                      </div>
                                                                    </div>
                                                                  }
                                                                </div>
                                                              }
                                                            </div>
